'use client'

import { SessionProvider } from 'next-auth/react'
import { Session } from 'next-auth'
import dynamic from 'next/dynamic'
import { useEffect } from 'react'
import { localSignOut, subscribeToLogout } from '@/lib/auth/services'

const OidcIframe = dynamic(() => import('@/components/common/OidcIframe'), {
  ssr: false,
})

// Logout detected in THIS tab (token refresh failed via onSessionError, or
// another tab broadcast a logout). Tear everything down and tell sibling
// tabs to do the same.
const handleSignedOut = () => {
  void localSignOut({ broadcast: true })
}

function CrossTabLogout() {
  useEffect(() => {
    // Logout broadcast from another tab → tear down locally (no rebroadcast).
    return subscribeToLogout(() => {
      void localSignOut({ broadcast: false })
    })
  }, [])
  return null
}

export default function AuthProvider({
  children,
  session,
}: {
  children: React.ReactNode
  session: Session | null
}) {
  return (
    <SessionProvider session={session}>
      <CrossTabLogout />
      {/* `onChanged` polls StackUp's OIDC check_session_iframe every 5s to
          detect "logged out elsewhere". It depends on the hidden iframe
          (auth.angelhack.com) reading its own session cookie while embedded in
          a hackathon.angelhack.com page — which only works while both share the
          registrable domain angelhack.com, making the iframe same-site rather
          than third-party. It was disabled for the window when hackathon had
          migrated to angelhack.com but auth had not: the cookie read silently
          failed, the check always reported "changed", and every logged-in user
          got signed out within seconds. If auth and hackathon ever end up on
          different registrable domains again, disable this first —
          NEXT_PUBLIC_DISABLE_OIDC_IFRAME=true is the no-deploy kill switch. */}
      {process.env.NEXT_PUBLIC_DISABLE_OIDC_IFRAME !== 'true' && (
        <OidcIframe
          onChanged={handleSignedOut}
          onSessionError={handleSignedOut}
        />
      )}
      {children}
    </SessionProvider>
  )
}
